<template>
  <div class="cb" v-if="!hidden">
    <!-- Launcher -->
    <button v-if="!open" class="cb-launcher" type="button" aria-label="Open the data assistant"
      @click="openPanel">
      <font-awesome-icon :icon="['fas', 'comment-dots']" />
      <span class="cb-launcher-label">Ask the database</span>
    </button>

    <!-- Panel -->
    <div v-else class="cb-panel" role="dialog" aria-label="Data assistant">
      <header class="cb-head">
        <div class="cb-head-id">
          <span class="cb-avatar">
            <font-awesome-icon :icon="['fas', 'robot']" />
          </span>
          <span class="cb-head-text">
            <span class="cb-head-title">Data Assistant</span>
            <span class="cb-head-sub">{{ headSubtitle }}</span>
          </span>
        </div>
        <div class="cb-head-actions">
          <button type="button" class="cb-icon-btn" title="Start over" aria-label="Start over"
            @click="restart">
            <font-awesome-icon :icon="['fas', 'undo']" />
          </button>
          <button type="button" class="cb-icon-btn" title="Close" aria-label="Close"
            @click="open = false">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </header>

      <!-- Applied filters -->
      <div v-if="summary.length" class="cb-chips-bar">
        <span v-for="item in summary" :key="item.key" class="cb-applied" :title="item.text">
          <b>{{ item.label }}:</b> {{ item.values.join(', ') }}
          <button type="button" class="cb-applied-x" :aria-label="`Remove ${item.label}`"
            @click="removeAnswer(item.key)">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </span>
      </div>

      <!-- Transcript -->
      <div ref="log" class="cb-log">
        <div v-for="msg in messages" :key="msg.id" class="cb-msg" :class="`cb-msg--${msg.from}`">
          <div class="cb-bubble" :class="{ 'cb-bubble--warn': msg.kind === 'warn' }">
            {{ msg.text }}
          </div>
        </div>

        <!-- Result card -->
        <div v-if="stage === 'ready'" class="cb-result">
          <div class="cb-result-count">
            <strong>{{ store.total.toLocaleString('en-CA') }}</strong>
            {{ store.total === 1 ? 'match' : 'matches' }}
          </div>

          <p v-if="!store.total" class="cb-result-empty">
            Nothing matched. Remove a filter above and try again.
          </p>

          <template v-else>
            <div class="cb-preview">
              <div v-for="row in previewRows" :key="row.utc_id" class="cb-preview-row">
                <span class="cb-preview-name">{{ row.project || '—' }}</span>
                <span class="cb-preview-meta">{{ row.city || '—' }} · {{ row.status || '—' }}</span>
              </div>
              <div v-if="store.total > previewRows.length" class="cb-preview-more">
                + {{ (store.total - previewRows.length).toLocaleString('en-CA') }} more
              </div>
            </div>

            <div class="cb-actions">
              <button type="button" class="cb-btn cb-btn--primary" @click="openResultsPage">
                <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                Open full results
              </button>
              <button type="button" class="cb-btn" :disabled="exporting" @click="download('pdf')">
                <font-awesome-icon :icon="['fas', 'file-pdf']" />
                PDF
              </button>
              <button type="button" class="cb-btn" :disabled="exporting" @click="download('csv')">
                <font-awesome-icon :icon="['fas', 'file-csv']" />
                CSV
              </button>
            </div>

            <button v-if="store.sql" type="button" class="cb-sql-toggle" @click="showSql = !showSql">
              <font-awesome-icon :icon="['fas', showSql ? 'chevron-down' : 'chevron-right']" />
              {{ showSql ? 'Hide' : 'Show' }} generated SQL
            </button>
            <pre v-if="showSql" class="cb-sql">{{ store.sql }}</pre>
          </template>

          <button type="button" class="cb-refine" @click="refine">
            <font-awesome-icon :icon="['fas', 'sliders-h']" />
            Change the answers
          </button>
        </div>

        <div v-if="store.loading" class="cb-typing">
          <span></span><span></span><span></span>
        </div>
      </div>

      <!-- Options for the open question -->
      <div v-if="stage === 'asking' && currentQuestion" class="cb-options">
        <input v-if="currentQuestion.options.length > OPTION_FILTER_THRESHOLD" v-model="optionFilter"
          class="cb-option-filter" type="text" :placeholder="`Filter ${currentQuestion.label.toLowerCase()}…`" />
        <div class="cb-option-list">
          <button v-for="opt in visibleOptions" :key="opt" type="button" class="cb-option"
            :class="{ 'cb-option--on': pending.includes(opt) }" @click="toggleOption(opt)">
            {{ opt }}
            <font-awesome-icon v-if="pending.includes(opt)" :icon="['fas', 'check']" class="cb-option-check" />
          </button>
          <span v-if="!visibleOptions.length" class="cb-option-none">
            No option matches “{{ optionFilter }}”.
          </span>
        </div>
        <div v-if="hiddenOptionCount" class="cb-option-hint">
          {{ hiddenOptionCount.toLocaleString('en-CA') }} more — type to narrow the list.
        </div>
      </div>

      <!-- Composer -->
      <footer class="cb-composer">
        <template v-if="stage === 'asking'">
          <input v-model="draft" class="cb-input" type="text" :placeholder="inputPlaceholder"
            @keyup.enter="submitDraft" />
          <button type="button" class="cb-send" :title="pending.length ? 'Apply and continue' : 'Send'"
            aria-label="Send" @click="submitDraft">
            <font-awesome-icon :icon="['fas', pending.length && !draft.trim() ? 'arrow-right' : 'paper-plane']" />
          </button>
          <button type="button" class="cb-skip" @click="skipQuestion">Skip</button>
        </template>
        <template v-else-if="stage === 'ready'">
          <button type="button" class="cb-btn cb-btn--wide" @click="refine">
            <font-awesome-icon :icon="['fas', 'sliders-h']" />
            Refine the search
          </button>
        </template>
        <p v-else-if="stage === 'noaccess'" class="cb-note">
          Your account does not have access to this data.
        </p>
        <p v-else class="cb-note">Loading filters…</p>
      </footer>

      <p v-if="store.error" class="cb-error">{{ store.error }}</p>
    </div>
  </div>
</template>

<script>
import { useChatBotStore } from '../../store/ChatBotStore.js';
import { useUserStore } from '../../store/UserStore.js';
import {
  CONTROL,
  controlWord,
  resolveAnswer,
  rejectionMessage,
  summariseAnswers,
} from '../../composables/chatbotConversation.js';
import { exportChatbotCsv, exportChatbotPdf } from '../../composables/chatbotExport.js';
import { CHATBOT_PREVIEW_LIMIT, canUseChatBot } from '../../variables.js';

// Routes that bring their own full-page chrome, or are not data pages at all.
// The bubble would sit on top of a login shell or a public landing page with
// nothing behind it to query.
const HIDDEN_ON = [
  '/',
  '/restricted',
  '/landing',
  '/presale-page',
  '/presale-database',
  '/demo',
  '/chatbot-results',
];

// Routes hidden by name rather than path, because their paths carry a
// parameter (/tasks/:cardRef, /people/:uid) and HIDDEN_ON matches exactly.
// Exact matching is deliberate — a substring test here once swallowed
// /project-reports along with /project — so a parameterised route needs the
// name, which cannot drift when the path changes.
//
// The worklist is task tracking, not a data page: there is nothing behind it
// to query, and the bubble covers the bottom of the Done lane.
const HIDDEN_ON_NAMED = ['tasks', 'task', 'taskperson'];

// Above this many options the chip list gets its own filter box. Status and
// building type are short enough to scan; the city list is a few hundred long.
const OPTION_FILTER_THRESHOLD = 12;

// How many chips to render before the filter box has narrowed things down.
// Painting several hundred buttons on every keystroke is what made the city
// question feel slow.
const OPTION_RENDER_CAP = 60;

// Rows shown inside the bubble. The full set is a separate request that only the
// results page and the exports make.
const PREVIEW_ROWS = 5;

export default {
  name: 'ChatBotWidget',

  data() {
    return {
      OPTION_FILTER_THRESHOLD,
      store: useChatBotStore(),
      open: false,
      // 'loading' | 'asking' | 'ready' | 'noaccess'
      stage: 'loading',
      index: 0,
      // Values picked for the question that is currently open, before they are
      // committed to the store. Held here so a multi-answer question can be
      // built up and still be abandoned by skipping.
      pending: [],
      draft: '',
      optionFilter: '',
      messages: [],
      messageSeq: 0,
      showSql: false,
      exporting: false,
    };
  },

  computed: {
    hidden() {
      const path = this.$route.path;
      if (HIDDEN_ON.some((p) => path === p)) return true;
      if (HIDDEN_ON_NAMED.includes(this.$route.name)) return true;
      // Admin roles only. The endpoints refuse every other tier outright
      // (presaledb_chatbot_allowed in chatbot_api.inc), so showing the launcher
      // more widely would only offer a conversation that cannot run a query —
      // this keeps the bubble off their screen rather than letting them find out
      // at the first question.
      return !canUseChatBot(useUserStore().getUser());
    },
    currentQuestion() {
      return this.store.questions[this.index] || null;
    },
    summary() {
      return summariseAnswers(this.store.answers, this.store.questions);
    },
    headSubtitle() {
      if (this.stage === 'noaccess') return 'No access';
      if (this.stage === 'ready') return `${this.summary.length} filter${this.summary.length === 1 ? '' : 's'} applied`;
      if (this.currentQuestion) {
        return `Question ${this.index + 1} of ${this.store.questions.length}`;
      }
      return 'Filters from the database';
    },
    // Chips for the open question: narrowed by the filter box, already-picked
    // values pinned to the front so a selection never scrolls out of sight, and
    // capped so a long list stays responsive.
    visibleOptions() {
      const q = this.currentQuestion;
      if (!q) return [];
      const needle = this.optionFilter.trim().toLowerCase();
      const matching = needle
        ? q.options.filter((o) => o.toLowerCase().includes(needle))
        : q.options;
      const picked = matching.filter((o) => this.pending.includes(o));
      const rest = matching.filter((o) => !this.pending.includes(o));
      return [...picked, ...rest].slice(0, OPTION_RENDER_CAP);
    },
    hiddenOptionCount() {
      const q = this.currentQuestion;
      if (!q) return 0;
      const needle = this.optionFilter.trim().toLowerCase();
      const total = needle
        ? q.options.filter((o) => o.toLowerCase().includes(needle)).length
        : q.options.length;
      return Math.max(0, total - this.visibleOptions.length);
    },
    inputPlaceholder() {
      const q = this.currentQuestion;
      if (!q) return 'Type a message…';
      return q.multiple
        ? `Type or pick — separate several with commas`
        : `Type or pick one ${q.label.toLowerCase()}`;
    },
    previewRows() {
      return this.store.rows.slice(0, PREVIEW_ROWS);
    },
  },

  methods: {
    say(text, from = 'bot', kind = 'plain') {
      this.messageSeq += 1;
      this.messages.push({ id: this.messageSeq, from, text, kind });
      this.$nextTick(this.scrollLog);
    },

    scrollLog() {
      const el = this.$refs.log;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async openPanel() {
      this.open = true;
      if (this.store.questionsLoaded) {
        this.$nextTick(this.scrollLog);
        return;
      }
      await this.store.loadQuestions();
      if (this.store.noAccess || !this.store.questions.length) {
        this.stage = 'noaccess';
        this.say('I could not load the filter list for your account.');
        return;
      }
      this.say(
        'Hi — I can search the project database using the same filters as the reports. I only accept answers that are inside those filters, so pick from the choices or type one. Say “skip” to leave a filter out.'
      );
      this.stage = 'asking';
      this.askCurrent();
    },

    askCurrent() {
      // No script at all — the filters request failed or the role has none.
      // Checked before the per-question branch below, which would otherwise
      // bounce into finish() and straight back here.
      if (!this.store.questions.length) {
        this.stage = 'noaccess';
        return;
      }
      const q = this.currentQuestion;
      if (!q) {
        this.finish();
        return;
      }
      this.optionFilter = '';
      this.pending = [...(this.store.answers[q.key] || [])];
      this.say(q.question);
    },

    toggleOption(option) {
      const q = this.currentQuestion;
      if (!q) return;
      if (!q.multiple) {
        this.pending = this.pending.includes(option) ? [] : [option];
        return;
      }
      const i = this.pending.indexOf(option);
      if (i === -1) this.pending.push(option);
      else this.pending.splice(i, 1);
    },

    // Enter / send. A typed message is resolved against the open question's
    // options first; with nothing typed, the chips already picked are committed.
    submitDraft() {
      const text = this.draft.trim();
      const q = this.currentQuestion;
      if (!q) return;

      if (!text) {
        if (!this.pending.length) {
          this.say(`Pick a ${q.label.toLowerCase()} below, or say “skip”.`, 'bot', 'warn');
          return;
        }
        this.commitAndAdvance();
        return;
      }

      this.say(text, 'user');
      this.draft = '';

      const control = controlWord(text);
      if (control === CONTROL.SKIP) {
        this.skipQuestion(false);
        return;
      }
      if (control === CONTROL.BACK) {
        this.goBack();
        return;
      }
      if (control === CONTROL.DONE) {
        this.commitAndAdvance(true);
        return;
      }

      const { accepted, rejected, ambiguous } = resolveAnswer(text, q);

      if (accepted.length) {
        this.pending = q.multiple
          ? [...new Set([...this.pending, ...accepted])]
          : [accepted[0]];
      }
      if (rejected.length || ambiguous.length) {
        this.say(rejectionMessage(q, rejected, ambiguous), 'bot', 'warn');
        // Something landed as well as being refused — keep the question open so
        // the user can see what was taken before moving on.
        if (accepted.length) {
          this.say(`Kept ${accepted.join(', ')}. Add more, or press the arrow to continue.`);
        }
        return;
      }

      // A clean single-answer question needs no confirmation step.
      if (!q.multiple) {
        this.commitAndAdvance();
        return;
      }
      this.say(
        `Added ${accepted.join(', ')}. Anything else for ${q.label.toLowerCase()}, or say “done”?`
      );
    },

    commitAndAdvance(fromDoneWord = false) {
      const q = this.currentQuestion;
      if (!q) return;
      if (!this.pending.length) {
        if (fromDoneWord) {
          this.skipQuestion(false);
          return;
        }
        this.say(`Pick a ${q.label.toLowerCase()} below, or say “skip”.`, 'bot', 'warn');
        return;
      }
      this.store.setAnswer(q.key, this.pending);
      this.say(`${q.label}: ${this.pending.join(', ')}`, 'user');
      this.advance();
    },

    skipQuestion(echo = true) {
      const q = this.currentQuestion;
      if (!q) return;
      if (echo) this.say('skip', 'user');
      this.store.clearAnswer(q.key);
      this.pending = [];
      this.advance();
    },

    advance() {
      this.index += 1;
      if (this.index >= this.store.questions.length) {
        this.finish();
        return;
      }
      this.askCurrent();
    },

    goBack() {
      if (this.index === 0) {
        this.say('That is the first question.', 'bot', 'warn');
        return;
      }
      this.index -= 1;
      this.stage = 'asking';
      this.askCurrent();
    },

    async finish() {
      this.stage = 'ready';
      this.showSql = false;

      if (!this.store.hasAnswers) {
        this.say(
          'Every filter was skipped, so there is nothing to search on. Pick at least one answer and I will run it.',
          'bot',
          'warn'
        );
        this.index = 0;
        this.stage = 'asking';
        this.askCurrent();
        return;
      }

      this.say('Turning that into a query…');
      const data = await this.store.runQuery({ limit: CHATBOT_PREVIEW_LIMIT });
      if (!data) return;
      if (this.store.rejected.length) {
        this.say(
          `Some answers were not accepted: ${this.store.rejected.join('; ')}`,
          'bot',
          'warn'
        );
      }
      this.say(
        this.store.total
          ? `Found ${this.store.total.toLocaleString('en-CA')} matching record${this.store.total === 1 ? '' : 's'}.`
          : 'Nothing matched those filters.'
      );
    },

    refine() {
      this.stage = 'asking';
      this.index = 0;
      this.askCurrent();
    },

    removeAnswer(key) {
      this.store.clearAnswer(key);
      const q = this.store.questions.find((item) => item.key === key);
      if (q) this.say(`Removed the ${q.label.toLowerCase()} filter.`);
      if (this.stage === 'ready') this.finish();
    },

    restart() {
      this.store.reset();
      this.messages = [];
      this.index = 0;
      this.pending = [];
      this.draft = '';
      this.optionFilter = '';
      this.showSql = false;
      this.stage = 'asking';
      this.say('Starting over.');
      this.askCurrent();
    },

    // The results page opens in a new tab, so the answers travel on its query
    // string rather than through the store — a new tab is a fresh Pinia, and a
    // linkable URL is worth more than shared state anyway.
    openResultsPage() {
      const { href } = this.$router.resolve({
        name: 'chatbot-results',
        query: { answers: JSON.stringify(this.store.answers) },
      });
      window.open(href, '_blank', 'noopener');
    },

    async download(format) {
      this.exporting = true;
      try {
        // Every matching row, not the preview the bubble is showing.
        const rows = await this.store.fetchAllRows();
        if (!rows.length) {
          this.say('There was nothing to export.', 'bot', 'warn');
          return;
        }
        if (format === 'csv') exportChatbotCsv(rows, this.summary, this.store.sql);
        else exportChatbotPdf(rows, this.summary, this.store.total);
        this.say(`Downloaded ${rows.length.toLocaleString('en-CA')} rows as ${format.toUpperCase()}.`);
      } catch (err) {
        console.error('Chat bot: export failed', err);
        this.say('The export could not be generated.', 'bot', 'warn');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.cb {
  --cb-surface: var(--rpd-white);
  --cb-surface-sunk: var(--rpd-bg-2);
  --cb-fg-1: var(--rpd-ink);
  --cb-fg-2: var(--rpd-muted);
  --cb-border: var(--rpd-line);
  --cb-accent: var(--rpd-coral);
  --cb-accent-fg: var(--rpd-white);
  --cb-bot-bg: var(--rpd-bg-2);
  --cb-user-bg: var(--rpd-navy-900);
  --cb-user-fg: var(--rpd-white);
  --cb-warn-bg: #FEF3E2;
  --cb-warn-fg: #8A5300;

  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1080;
  font-family: var(--rpd-font-sans);
}

/* ── Launcher ── */
.cb-launcher {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: var(--rpd-r-pill);
  background: var(--cb-accent);
  color: var(--cb-accent-fg);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--rpd-shadow-md);
  transition: transform 0.15s, background 0.15s;
}

.cb-launcher:hover {
  background: var(--rpd-coral-dark);
  transform: translateY(-1px);
}

/* ── Panel ── */
.cb-panel {
  display: flex;
  flex-direction: column;
  width: 400px;
  max-width: calc(100vw - 32px);
  height: 580px;
  max-height: calc(100vh - 40px);
  background: var(--cb-surface);
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-lg);
  box-shadow: var(--rpd-shadow-lg);
  overflow: hidden;
}

.cb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  background: var(--cb-user-bg);
  color: var(--rpd-white);
  flex-shrink: 0;
}

.cb-head-id {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cb-avatar {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--rpd-r-pill);
  background: var(--cb-accent);
  font-size: 14px;
  flex-shrink: 0;
}

.cb-head-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cb-head-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}

.cb-head-sub {
  font-size: 11px;
  color: var(--rpd-navy-300);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cb-head-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.cb-icon-btn {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  font-size: 13px;
}

.cb-icon-btn:hover {
  background: rgba(255, 255, 255, 0.14);
  color: var(--rpd-white);
}

/* ── Applied filters ── */
.cb-chips-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 8px 12px;
  background: var(--cb-surface-sunk);
  border-bottom: 1px solid var(--cb-border);
  max-height: 92px;
  overflow-y: auto;
  flex-shrink: 0;
}

.cb-applied {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 3px 6px 3px 9px;
  border-radius: var(--rpd-r-pill);
  background: var(--cb-surface);
  border: 1px solid var(--cb-border);
  font-size: 11px;
  color: var(--cb-fg-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cb-applied b {
  font-weight: 600;
  color: var(--cb-fg-2);
}

.cb-applied-x {
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: var(--rpd-r-pill);
  background: transparent;
  color: var(--cb-fg-2);
  cursor: pointer;
  font-size: 9px;
  flex-shrink: 0;
}

.cb-applied-x:hover {
  background: var(--cb-accent);
  color: var(--rpd-white);
}

/* ── Transcript ── */
.cb-log {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--cb-surface);
}

.cb-msg {
  display: flex;
}

.cb-msg--user {
  justify-content: flex-end;
}

.cb-bubble {
  max-width: 86%;
  padding: 9px 12px;
  border-radius: var(--rpd-r-md);
  background: var(--cb-bot-bg);
  color: var(--cb-fg-1);
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.cb-msg--user .cb-bubble {
  background: var(--cb-user-bg);
  color: var(--cb-user-fg);
}

.cb-bubble--warn {
  background: var(--cb-warn-bg);
  color: var(--cb-warn-fg);
}

.cb-typing {
  display: flex;
  gap: 4px;
  padding: 4px 2px;
}

.cb-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--cb-fg-2);
  animation: cb-bounce 1.2s infinite ease-in-out;
}

.cb-typing span:nth-child(2) {
  animation-delay: 0.15s;
}

.cb-typing span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes cb-bounce {

  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }

  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* ── Result card ── */
.cb-result {
  margin-top: 4px;
  padding: 12px;
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-md);
  background: var(--cb-surface-sunk);
}

.cb-result-count {
  font-size: 13px;
  color: var(--cb-fg-2);
  margin-bottom: 8px;
}

.cb-result-count strong {
  font-size: 20px;
  font-weight: 700;
  color: var(--cb-fg-1);
  margin-right: 4px;
}

.cb-result-empty {
  margin: 0;
  font-size: 12px;
  color: var(--cb-fg-2);
}

.cb-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.cb-preview-row {
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  border-radius: var(--rpd-r-sm);
  background: var(--cb-surface);
  border: 1px solid var(--cb-border);
}

.cb-preview-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--cb-fg-1);
}

.cb-preview-meta {
  font-size: 11px;
  color: var(--cb-fg-2);
}

.cb-preview-more {
  font-size: 11px;
  color: var(--cb-fg-2);
  text-align: center;
}

.cb-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-sm);
  background: var(--cb-surface);
  color: var(--cb-fg-1);
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
}

.cb-btn:hover:not(:disabled) {
  border-color: var(--cb-accent);
  color: var(--cb-accent);
}

.cb-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cb-btn--primary {
  background: var(--cb-accent);
  border-color: var(--cb-accent);
  color: var(--rpd-white);
  flex: 1 1 100%;
}

.cb-btn--primary:hover:not(:disabled) {
  background: var(--rpd-coral-dark);
  border-color: var(--rpd-coral-dark);
  color: var(--rpd-white);
}

.cb-btn--wide {
  width: 100%;
}

.cb-sql-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 0;
  border: none;
  background: none;
  color: var(--cb-fg-2);
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
}

.cb-sql-toggle:hover {
  color: var(--cb-accent);
}

.cb-sql {
  margin: 6px 0 0;
  padding: 8px;
  max-height: 180px;
  overflow: auto;
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-navy-900);
  color: #d6e4f0;
  font-family: var(--rpd-font-mono);
  font-size: 10px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.cb-refine {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 0;
  border: none;
  background: none;
  color: var(--cb-fg-2);
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
}

.cb-refine:hover {
  color: var(--cb-accent);
}

/* ── Options ── */
.cb-options {
  flex-shrink: 0;
  padding: 8px 12px;
  border-top: 1px solid var(--cb-border);
  background: var(--cb-surface-sunk);
}

.cb-option-filter {
  width: 100%;
  height: 30px;
  margin-bottom: 7px;
  padding: 0 10px;
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-sm);
  background: var(--cb-surface);
  color: var(--cb-fg-1);
  font-size: 12px;
  font-family: inherit;
}

.cb-option-filter:focus {
  outline: none;
  border-color: var(--cb-accent);
}

.cb-option-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-height: 116px;
  overflow-y: auto;
}

.cb-option {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-pill);
  background: var(--cb-surface);
  color: var(--cb-fg-1);
  font-size: 11.5px;
  font-family: inherit;
  cursor: pointer;
}

.cb-option:hover {
  border-color: var(--cb-accent);
  color: var(--cb-accent);
}

.cb-option--on {
  background: var(--cb-accent);
  border-color: var(--cb-accent);
  color: var(--rpd-white);
}

.cb-option--on:hover {
  background: var(--rpd-coral-dark);
  color: var(--rpd-white);
}

.cb-option-check {
  font-size: 9px;
}

.cb-option-none,
.cb-option-hint {
  font-size: 11px;
  color: var(--cb-fg-2);
}

.cb-option-hint {
  display: block;
  margin-top: 6px;
}

/* ── Composer ── */
.cb-composer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--cb-border);
  background: var(--cb-surface);
  flex-shrink: 0;
}

.cb-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--cb-border);
  border-radius: var(--rpd-r-pill);
  background: var(--cb-surface);
  color: var(--cb-fg-1);
  font-size: 13px;
  font-family: inherit;
}

.cb-input:focus {
  outline: none;
  border-color: var(--cb-accent);
}

.cb-send {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--rpd-r-pill);
  background: var(--cb-accent);
  color: var(--rpd-white);
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
}

.cb-send:hover {
  background: var(--rpd-coral-dark);
}

.cb-skip {
  border: none;
  background: none;
  color: var(--cb-fg-2);
  font-size: 11.5px;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
}

.cb-skip:hover {
  color: var(--cb-accent);
}

.cb-note {
  margin: 0;
  font-size: 12px;
  color: var(--cb-fg-2);
}

.cb-error {
  margin: 0;
  padding: 8px 12px;
  background: #FDECEC;
  color: #A32B33;
  font-size: 12px;
  flex-shrink: 0;
}

/* ── Responsive: full-bleed sheet on small screens ── */
@media (max-width: 575px) {
  .cb {
    right: 12px;
    bottom: 12px;
    left: 12px;
  }

  .cb-launcher {
    height: 44px;
    padding: 0 16px;
  }

  .cb-launcher-label {
    display: none;
  }

  .cb-panel {
    width: 100%;
    height: calc(100vh - 24px);
  }
}

/* ── Dark mode ── */
html.dark .cb {
  --cb-surface: #152030;
  --cb-surface-sunk: #0e1b2c;
  --cb-fg-1: #d6e4f0;
  --cb-fg-2: rgba(255, 255, 255, 0.6);
  --cb-border: rgba(255, 255, 255, 0.1);
  --cb-bot-bg: #0e1b2c;
  --cb-user-bg: #1e3753;
  --cb-user-fg: #eaf2fa;
  --cb-warn-bg: rgba(244, 183, 64, 0.14);
  --cb-warn-fg: #f4b740;
}

html.dark .cb-head {
  background: #0e1b2c;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

html.dark .cb-sql {
  background: #060d17;
}

html.dark .cb-error {
  background: rgba(242, 106, 111, 0.14);
  color: #f8a0a3;
}
</style>
